/**
 * Request/response schemas for the orthopedics specialty module (`BACKLOG.md` M7). Same reasoning as
 * `dermatologyRequests.ts`/`aestheticsRequests.ts`: these live in `shared/` so the server validates
 * against the exact schema the web app infers its types from, every object schema is strict so an
 * unrecognised key is a rejection rather than silently ignored, and no server-owned value (an id,
 * `orgId`, `enteredByUserId`/`recordedByUserId`, a timestamp) appears in any request schema.
 *
 * **Two values are deliberately never accepted from the client, even though they could technically be
 * sent along with a create request** — both computed server-side from data the server already has, per
 * `claude-kit/rules/invariants.md`'s "server-authoritative values never come from the request":
 *
 *   - `orthopedicsGlobalSurgicalPeriods.globalPeriodDays` — looked up from `cptCode` via
 *     `findOrthoGlobalPeriodCptCode`, never trusted from the caller, so a client cannot claim a 90-day
 *     procedure carries only a 10-day (or no) global period.
 *   - Every parsed UDI field (`deviceIdentifier`/`lotNumber`/`serialNumber`/`expirationDate`/
 *     `manufactureDate`) — derived from `rawBarcode` via `parseUdi`, never accepted independently, so
 *     the parsed structure can never disagree with the raw capture it was supposedly parsed from.
 */

import { z } from 'zod';

import { calendarDateSchema, confidentialityReasonsInputSchema } from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { BODY_MAP_REGIONS, BODY_MAP_SIDES } from './bodyMapping.ts';
import {
  CONDITION_TYPES,
  templateFieldFor,
  type ConditionType,
} from './orthopedics/conditionTemplates.ts';
import {
  EXAM_MANEUVERS,
  LACHMAN_GRADES,
  MANEUVER_RESULTS,
  maneuverSupportsGrade,
} from './orthopedics/examManeuvers.ts';
import { isProInstrumentId } from './orthopedics/patientReportedOutcomes.ts';
import {
  GLOBAL_PERIOD_RELATIONSHIPS,
  ORTHO_GLOBAL_PERIOD_CPT_CODE_VALUES,
} from './orthopedics/globalSurgicalPeriod.ts';

const trimmed = (max: number) => z.string().trim().min(1).max(max);

export const MAX_TEMPLATE_FIELD_VALUE_LENGTH = 200;
export const MAX_TEMPLATE_FIELD_COUNT = 20;
export const MAX_RAW_SCORE_LENGTH = 50;
export const MAX_NOTES_LENGTH = 500;
export const MAX_RAW_BARCODE_LENGTH = 500;

// --- Condition episodes -----------------------------------------------------------------------------

const templateFieldsSchema = z.record(z.string(), trimmed(MAX_TEMPLATE_FIELD_VALUE_LENGTH));

/** Cross-checks every `templateFields` entry against `conditionTemplates.ts`'s own per-condition field
 * list — the one place a request's free-form `Record<string, string>` is actually validated against
 * the closed shape `templateFieldFor` describes, since Zod's own `.record()` cannot express "keys and
 * values depend on a sibling field" declaratively. */
function checkTemplateFields(
  conditionType: ConditionType,
  templateFields: Record<string, string>,
  ctx: z.RefinementCtx,
): void {
  const keys = Object.keys(templateFields);
  if (keys.length > MAX_TEMPLATE_FIELD_COUNT) {
    ctx.addIssue({
      code: 'custom',
      path: ['templateFields'],
      message: `at most ${String(MAX_TEMPLATE_FIELD_COUNT)} template fields`,
    });
  }
  for (const [key, value] of Object.entries(templateFields)) {
    const field = templateFieldFor(conditionType, key);
    if (field === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['templateFields', key],
        message: `"${key}" is not a recognised template field for conditionType "${conditionType}"`,
      });
      continue;
    }
    if (field.values !== undefined && !field.values.includes(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['templateFields', key],
        message: `"${value}" is not one of the allowed values for "${key}"`,
      });
    }
  }
}

export const createConditionEpisodeRequestSchema = z
  .object({
    conditionType: z.enum(CONDITION_TYPES),
    bodyMapRegion: z.enum(BODY_MAP_REGIONS),
    bodyMapSide: z.enum(BODY_MAP_SIDES).nullable().default(null),
    diagnosedOn: calendarDateSchema,
    templateFields: templateFieldsSchema.default({}),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    checkTemplateFields(value.conditionType, value.templateFields, ctx);
  });
export type CreateConditionEpisodeRequest = z.infer<typeof createConditionEpisodeRequestSchema>;

export const conditionEpisodeResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  conditionType: z.string(),
  bodyMapRegion: z.string(),
  bodyMapSide: z.string().nullable(),
  diagnosedOn: calendarDateSchema,
  templateFields: z.record(z.string(), z.string()),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type ConditionEpisodeResponse = z.infer<typeof conditionEpisodeResponseSchema>;

export const conditionEpisodeEnvelopeSchema = z.object({
  conditionEpisode: conditionEpisodeResponseSchema,
});
export const conditionEpisodeListEnvelopeSchema = z.object({
  conditionEpisodes: z.array(conditionEpisodeResponseSchema),
});

// --- Exam maneuvers ----------------------------------------------------------------------------------

export const createExamManeuverRequestSchema = z
  .object({
    maneuver: z.enum(EXAM_MANEUVERS),
    result: z.enum(MANEUVER_RESULTS),
    grade: z.enum(LACHMAN_GRADES).nullable().default(null),
    bodyMapRegion: z.enum(BODY_MAP_REGIONS),
    bodyMapSide: z.enum(BODY_MAP_SIDES).nullable().default(null),
    performedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .refine((value) => value.grade === null || maneuverSupportsGrade(value.maneuver), {
    message: 'grade is only meaningful for a maneuver that conventionally supports one (lachman)',
    path: ['grade'],
  });
export type CreateExamManeuverRequest = z.infer<typeof createExamManeuverRequestSchema>;

export const examManeuverResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  maneuver: z.string(),
  result: z.string(),
  grade: z.string().nullable(),
  bodyMapRegion: z.string(),
  bodyMapSide: z.string().nullable(),
  performedOn: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type ExamManeuverResponse = z.infer<typeof examManeuverResponseSchema>;

export const examManeuverEnvelopeSchema = z.object({ examManeuver: examManeuverResponseSchema });
export const examManeuverListEnvelopeSchema = z.object({
  examManeuvers: z.array(examManeuverResponseSchema),
});

// --- Patient-reported outcomes -----------------------------------------------------------------------

const instrumentIdSchema = z.string().refine(isProInstrumentId, {
  message: 'not a recognised PRO instrument id',
});

export const createPatientReportedOutcomeRequestSchema = z
  .object({
    instrumentId: instrumentIdSchema,
    administeredOn: calendarDateSchema,
    /** Free text — an instrument's own scoring scale varies too much (a raw sum, a T-score, a
     * category) to force into one numeric column; see `patientReportedOutcomes.ts`'s header on why
     * this module never computes a score itself. */
    rawScore: trimmed(MAX_RAW_SCORE_LENGTH),
    notes: trimmed(MAX_NOTES_LENGTH).optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreatePatientReportedOutcomeRequest = z.infer<
  typeof createPatientReportedOutcomeRequestSchema
>;

export const patientReportedOutcomeResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  instrumentId: z.string(),
  administeredOn: calendarDateSchema,
  rawScore: z.string(),
  notes: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type PatientReportedOutcomeResponse = z.infer<typeof patientReportedOutcomeResponseSchema>;

export const patientReportedOutcomeEnvelopeSchema = z.object({
  patientReportedOutcome: patientReportedOutcomeResponseSchema,
});
export const patientReportedOutcomeListEnvelopeSchema = z.object({
  patientReportedOutcomes: z.array(patientReportedOutcomeResponseSchema),
});

// --- Global surgical periods -------------------------------------------------------------------------

export const createGlobalSurgicalPeriodRequestSchema = z
  .object({
    cptCode: z.enum(ORTHO_GLOBAL_PERIOD_CPT_CODE_VALUES),
    procedureDate: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateGlobalSurgicalPeriodRequest = z.infer<
  typeof createGlobalSurgicalPeriodRequestSchema
>;

export const globalSurgicalPeriodResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  cptCode: z.string(),
  procedureDate: calendarDateSchema,
  /** Server-computed from `cptCode` at write time — see this module's own header. */
  globalPeriodDays: z.number().int(),
  windowEndDate: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type GlobalSurgicalPeriodResponse = z.infer<typeof globalSurgicalPeriodResponseSchema>;

export const globalSurgicalPeriodEnvelopeSchema = z.object({
  globalSurgicalPeriod: globalSurgicalPeriodResponseSchema,
});
export const globalSurgicalPeriodListEnvelopeSchema = z.object({
  globalSurgicalPeriods: z.array(globalSurgicalPeriodResponseSchema),
});

/** `GET .../global-surgical-periods/:id/modifier?serviceDate=...&relationship=...` — a read-only
 * query against `@emr/shared`'s own `determineGlobalPeriodModifier`, never a stored value: recomputed
 * every time so a stale window can never be cached into a wrong modifier. */
export const globalPeriodModifierQuerySchema = z.object({
  serviceDate: calendarDateSchema,
  relationship: z.enum(GLOBAL_PERIOD_RELATIONSHIPS),
});
export type GlobalPeriodModifierQuery = z.infer<typeof globalPeriodModifierQuerySchema>;

export const globalPeriodModifierResponseSchema = z.object({
  ok: z.boolean(),
  modifier: z.string().nullable(),
  reason: z.string(),
});
export const globalPeriodModifierEnvelopeSchema = z.object({
  result: globalPeriodModifierResponseSchema,
});

// --- Implant UDI records ---------------------------------------------------------------------------

export const createImplantUdiRecordRequestSchema = z
  .object({
    rawBarcode: trimmed(MAX_RAW_BARCODE_LENGTH),
    implantedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateImplantUdiRecordRequest = z.infer<typeof createImplantUdiRecordRequestSchema>;

export const implantUdiRecordResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  rawBarcode: z.string(),
  barcodeFormat: z.string(),
  deviceIdentifier: z.string().nullable(),
  lotNumber: z.string().nullable(),
  serialNumber: z.string().nullable(),
  expirationDate: calendarDateSchema.nullable(),
  manufactureDate: calendarDateSchema.nullable(),
  implantedOn: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type ImplantUdiRecordResponse = z.infer<typeof implantUdiRecordResponseSchema>;

export const implantUdiRecordEnvelopeSchema = z.object({
  implantUdiRecord: implantUdiRecordResponseSchema,
});
export const implantUdiRecordListEnvelopeSchema = z.object({
  implantUdiRecords: z.array(implantUdiRecordResponseSchema),
});

// --- Failure vocabulary -----------------------------------------------------------------------

/** Closed union so the web app can branch on a code without matching on prose — extends
 * `encounterRequests.ts`'s `CLINICAL_ERROR_CODES`, matching `dermatologyRequests.ts`'s own
 * `DERMATOLOGY_ERROR_CODES` pattern. */
export const ORTHOPEDICS_ERROR_CODES = [...CLINICAL_ERROR_CODES, 'not_entitled'] as const;
export type OrthopedicsErrorCode = (typeof ORTHOPEDICS_ERROR_CODES)[number];
